import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';
import { ArrowLeft, Timer, Target, Zap } from 'lucide-react';

interface FlipCard {
  id: number;
  x: number;
  y: number;
  color: string;
  flipped: boolean;
  targetColor: string;
}

export default function FlipTrackingLevel() {
  const navigate = useNavigate();
  const { contrastLevel, speedLevel, addEnergy, completeLevel, unlockLevel } = useGameStore();
  const [cards, setCards] = useState<FlipCard[]>([]);
  const [targetColor, setTargetColor] = useState('');
  const [score, setScore] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [round, setRound] = useState(0);
  const [totalRounds] = useState(10);
  const [gameState, setGameState] = useState<'ready' | 'playing' | 'flipping' | 'choosing' | 'complete'>('ready');
  const [reactionTimes, setReactionTimes] = useState<number[]>([]);
  const [roundStartTime, setRoundStartTime] = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [showResult, setShowResult] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const colors = [
    `rgba(239, 68, 68, ${contrastLevel})`,
    `rgba(34, 197, 94, ${contrastLevel})`,
    `rgba(59, 130, 246, ${contrastLevel})`,
    `rgba(251, 191, 36, ${contrastLevel})`,
    `rgba(168, 85, 247, ${contrastLevel})`,
    `rgba(236, 72, 153, ${contrastLevel})`,
  ];

  const generateRound = useCallback(() => {
    const gridSize = 3;
    const newCards: FlipCard[] = [];
    const target = colors[Math.floor(Math.random() * colors.length)];
    setTargetColor(target);

    for (let i = 0; i < gridSize * gridSize; i++) {
      const row = Math.floor(i / gridSize);
      const col = i % gridSize;
      newCards.push({
        id: i,
        x: col * 33 + 5,
        y: row * 33 + 5,
        color: colors[Math.floor(Math.random() * colors.length)],
        flipped: false,
        targetColor: target,
      });
    }
    // Ensure at least one card has the target color
    const randomIdx = Math.floor(Math.random() * newCards.length);
    newCards[randomIdx].color = target;

    setCards(newCards);
    setGameState('flipping');

    // Show cards briefly, then flip them
    setTimeout(() => {
      setCards(prev => prev.map(c => ({ ...c, flipped: true })));
      setTimeout(() => {
        // Shuffle positions
        const shuffled = [...newCards].map((c, _i) => ({
          ...c,
          flipped: false,
          x: (Math.random() * 70 + 5),
          y: (Math.random() * 60 + 15),
        }));
        setCards(shuffled);
        setGameState('choosing');
        setRoundStartTime(Date.now());
      }, 1000 / speedLevel);
    }, 1500 / speedLevel);
  }, [colors, contrastLevel, speedLevel]);

  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setMistakes(0);
    setRound(0);
    setReactionTimes([]);
    setTimeLeft(60);
    generateRound();
  };

  useEffect(() => {
    if (gameState === 'playing' || gameState === 'flipping' || gameState === 'choosing') {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setGameState('complete');
            setShowResult(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [gameState]);

  const handleCardClick = (card: FlipCard) => {
    if (gameState !== 'choosing') return;

    const reactionTime = Date.now() - roundStartTime;
    setReactionTimes(prev => [...prev, reactionTime]);

    if (card.color === targetColor) {
      setScore(s => s + 10);
      setRound(r => {
        const newRound = r + 1;
        if (newRound >= totalRounds) {
          setGameState('complete');
          setShowResult(true);
        } else {
          generateRound();
        }
        return newRound;
      });
    } else {
      setMistakes(m => m + 1);
      setScore(s => Math.max(0, s - 5));
      // shake feedback
    }
  };

  const handleComplete = () => {
    const avgReaction = reactionTimes.length > 0 
      ? reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length 
      : 0;
    const accuracy = round > 0 ? ((round - mistakes) / round) * 100 : 0;
    
    const energyEarned = Math.floor(score / 5) + Math.floor(accuracy / 10);
    addEnergy(energyEarned);
    
    completeLevel({
      levelId: 'flip-tracking',
      levelName: '翻转拍视觉追踪',
      score,
      accuracy: Math.max(0, accuracy),
      reactionTime: avgReaction,
      completedAt: new Date().toISOString(),
      contrast: contrastLevel,
      speed: speedLevel,
    });

    unlockLevel('firefly-chase');
    navigate('/report');
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#0a1a10] via-[#122a1a] to-[#1a3525] relative overflow-hidden">
      {/* Header */}
      <header className="relative z-10 px-4 pt-6 pb-2">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <button onClick={() => navigate('/')} className="text-white/60 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold text-white">🔄 翻转拍追踪</h1>
          <div className="flex items-center gap-2">
            <Timer className="w-4 h-4 text-white/60" />
            <span className={`text-sm font-bold ${timeLeft <= 10 ? 'text-red-400' : 'text-white/80'}`}>{timeLeft}s</span>
          </div>
        </div>
        {/* Progress */}
        <div className="max-w-lg mx-auto mt-3">
          <div className="flex items-center justify-between text-xs text-white/50 mb-1">
            <span>第 {round}/{totalRounds} 轮</span>
            <span className="flex items-center gap-1"><Target className="w-3 h-3" /> {score}分</span>
          </div>
          <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-gradient-to-r from-[#4a9e6b] to-[#7dffa8] rounded-full"
              animate={{ width: `${(round / totalRounds) * 100}%` }}
            />
          </div>
        </div>
      </header>

      {/* Game Area */}
      <div className="relative z-10 px-4 mt-4">
        <div className="max-w-lg mx-auto">
          {gameState === 'ready' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <div className="text-6xl mb-6">🔄</div>
              <h2 className="text-xl font-bold text-white mb-3">翻转拍视觉追踪</h2>
              <p className="text-white/60 text-sm mb-2 max-w-xs mx-auto">
                色块会先展示颜色，然后翻转并移动位置。
              </p>
              <p className="text-white/60 text-sm mb-8 max-w-xs mx-auto">
                记住目标颜色的色块，在它们翻转后找到正确的那个！
              </p>
              <button onClick={startGame} className="btn-primary text-lg">
                <Zap className="w-5 h-5 inline mr-2" />开始训练
              </button>
            </motion.div>
          )}

          {(gameState === 'playing' || gameState === 'flipping' || gameState === 'choosing') && (
            <div className="relative">
              {/* Target indicator */}
              <div className="text-center mb-4">
                <span className="text-xs text-white/60">找到这个颜色的色块:</span>
                <div 
                  className="w-10 h-10 rounded-lg mx-auto mt-2 border-2 border-white/30"
                  style={{ backgroundColor: targetColor }}
                />
              </div>
              
              {/* Game grid */}
              <div className="relative w-full aspect-square max-w-[350px] mx-auto rounded-2xl border border-white/10 bg-black/20">
                <AnimatePresence>
                  {cards.map(card => (
                    <motion.button
                      key={card.id}
                      className="absolute w-[28%] h-[28%] rounded-xl border-2 border-white/20 transition-colors"
                      style={{
                        left: `${card.x}%`,
                        top: `${card.y}%`,
                        backgroundColor: card.flipped ? '#374151' : card.color,
                      }}
                      animate={{
                        left: `${card.x}%`,
                        top: `${card.y}%`,
                        rotateY: card.flipped ? 180 : 0,
                      }}
                      transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleCardClick(card)}
                    >
                      {card.flipped && (
                        <span className="text-white/30 text-lg">?</span>
                      )}
                    </motion.button>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Result Modal */}
      <AnimatePresence>
        {showResult && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          >
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              className="game-card p-6 max-w-sm w-full text-center"
            >
              <div className="text-4xl mb-4">🎉</div>
              <h2 className="text-xl font-bold text-white mb-4">训练完成！</h2>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">得分</p>
                  <p className="text-lg font-bold text-[#7dffa8]">{score}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">完成轮次</p>
                  <p className="text-lg font-bold text-[#fbbf24]">{round}/{totalRounds}</p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">平均反应</p>
                  <p className="text-lg font-bold text-[#60a5fa]">
                    {reactionTimes.length > 0 ? Math.round(reactionTimes.reduce((a,b) => a+b, 0) / reactionTimes.length) : 0}ms
                  </p>
                </div>
                <div className="bg-white/5 rounded-xl p-3">
                  <p className="text-xs text-white/50">获得能量</p>
                  <p className="text-lg font-bold text-[#fbbf24]">
                    +{Math.floor(score / 5) + Math.floor(((round - mistakes) / Math.max(round, 1)) * 10)}
                  </p>
                </div>
              </div>
              <button onClick={handleComplete} className="btn-primary w-full">
                查看训练报告 📊
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
